import { useEffect, useState } from "react";

import { ArrowUpRight, Eye, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field } from "@/components/cinch/Field";
import { ScaleInput } from "@/components/cinch/ScaleInput";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useCan } from "@/lib/auth/useCan";
import { useUser } from "@/lib/auth/useUser";

import {
  useDeleteNote,
  useOptions,
  useRevealNote,
  useSelectNote,
  useUpdateNote,
  useVoteNote,
  type Note,
  type NoteUpdateBody,
  type Workspace,
} from "../api";
import { IMPACT_CATEGORIES, type ImpactCategory } from "../constants";
import { PromoteObjectiveDialog } from "./PromoteObjectiveDialog";

/** Everything about one initiative: content, impact-category color,
 *  departments, goal tags, blind voting, reveal, selection, promotion. */
export function GridNoteDialog({
  note,
  workspace,
  onOpenChange,
}: {
  note: Note | null;
  workspace: Workspace;
  onOpenChange: (open: boolean) => void;
}) {
  const user = useUser();
  const isAdmin = useCan("planning.admin");
  const options = useOptions();
  const update = useUpdateNote(workspace.session.id);
  const vote = useVoteNote(workspace.session.id);
  const reveal = useRevealNote(workspace.session.id);
  const select = useSelectNote(workspace.session.id);
  const del = useDeleteNote(workspace.session.id);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [effort, setEffort] = useState<number | null>(null);
  const [impact, setImpact] = useState<number | null>(null);
  const [promoteOpen, setPromoteOpen] = useState(false);

  useEffect(() => {
    if (note) {
      setTitle(note.title);
      setDescription(note.description ?? "");
      setEffort(note.vote_summary.my_vote?.effort ?? null);
      setImpact(note.vote_summary.my_vote?.impact ?? null);
    }
  }, [note?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!note) return null;
  const canEdit = isAdmin || note.author_id === user.id;
  const vs = note.vote_summary;
  const dirty = title !== note.title || description !== (note.description ?? "");
  const voteDirty =
    effort !== null &&
    impact !== null &&
    (effort !== vs.my_vote?.effort || impact !== vs.my_vote?.impact);

  const patch = (body: NoteUpdateBody) =>
    update.mutate(
      { id: note.id, ...body },
      { onError: (err) => toast.error(err.message) },
    );

  return (
    <Dialog open={note !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {note.title}
            {note.selected ? (
              <Star className="h-4 w-4 fill-warning text-warning" />
            ) : null}
          </DialogTitle>
          <DialogDescription>
            {note.author_name ? `By ${note.author_name}` : "Initiative"}
            {note.source_category_id ? " · promoted from a SWOT theme" : ""}
            {note.promoted_objective_id ? " · already an objective" : ""}
          </DialogDescription>
        </DialogHeader>

        {canEdit ? (
          <div className="space-y-3">
            <Field
              id="grid-note-title"
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Textarea
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
            {dirty ? (
              <Button
                size="sm"
                onClick={() =>
                  patch({ title: title.trim(), description: description.trim() || null })
                }
                disabled={!title.trim() || update.isPending}
              >
                Save changes
              </Button>
            ) : null}
          </div>
        ) : note.description ? (
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{note.description}</p>
        ) : null}

        {/* ── Company-impact color + departments + goals ── */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-muted-foreground">Impact category</div>
            <Select
              value={note.impact_category ?? ""}
              onValueChange={(v) => patch({ impact_category: v as ImpactCategory })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pick a color…" />
              </SelectTrigger>
              <SelectContent>
                {IMPACT_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-muted-foreground">
              Departments involved ({note.departments.length})
            </div>
            <div className="flex max-h-28 flex-col gap-1 overflow-y-auto rounded-md border p-2">
              {workspace.departments.map((d) => {
                const checked = note.departments.includes(d.name);
                return (
                  <label key={d.id} className="flex items-center gap-2 text-xs">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) =>
                        patch({
                          departments: v
                            ? [...note.departments, d.name]
                            : note.departments.filter((x) => x !== d.name),
                        })
                      }
                    />
                    {d.name}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {options.data?.goals.length ? (
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-muted-foreground">
              Company goals this would move
            </div>
            <div className="flex flex-wrap gap-1.5">
              {options.data.goals.map((g) => {
                const tagged = note.goal_ids.includes(g.id);
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() =>
                      patch({
                        goal_ids: tagged
                          ? note.goal_ids.filter((x) => x !== g.id)
                          : [...note.goal_ids, g.id],
                      })
                    }
                  >
                    <Badge
                      variant={tagged ? "default" : "outline"}
                      className="cursor-pointer text-[11px]"
                    >
                      {g.title} · {g.year}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* ── Voting ── */}
        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Your scores
            </div>
            <div className="text-xs text-muted-foreground">
              {vs.count} ballot{vs.count === 1 ? "" : "s"}
              {vs.revealed ? " · revealed" : " · hidden until reveal"}
            </div>
          </div>
          <ScaleInput label="Impact" value={impact} onChange={setImpact} />
          <ScaleInput label="Effort" value={effort} onChange={setEffort} />
          <div className="flex items-center justify-between pt-1">
            <Button
              size="sm"
              disabled={!voteDirty || vote.isPending}
              onClick={() =>
                vote.mutate(
                  { id: note.id, effort: effort!, impact: impact! },
                  {
                    onSuccess: () => toast.success("Vote cast"),
                    onError: (err) => toast.error(err.message),
                  },
                )
              }
            >
              {vs.my_vote ? "Update vote" : "Cast vote"}
            </Button>
            {isAdmin && !vs.revealed ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  reveal.mutate(note.id, {
                    onSuccess: () => toast.success("Votes revealed — note placed"),
                    onError: (err) => toast.error(err.message),
                  })
                }
              >
                <Eye className="mr-1 h-3.5 w-3.5" /> Reveal
              </Button>
            ) : null}
          </div>
          {vs.revealed && vs.votes.length ? (
            <div className="space-y-1 pt-1">
              <div className="text-xs font-medium">
                Average: impact {vs.avg_impact} · effort {vs.avg_effort}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                {vs.votes.map((v) => (
                  <span key={v.user_id}>
                    {v.user_name}: I{v.impact} / E{v.effort}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter className="items-center gap-2 sm:justify-between">
          <div className="flex gap-1">
            {canEdit ? (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() =>
                  del.mutate(note.id, {
                    onSuccess: () => {
                      toast.success("Initiative deleted");
                      onOpenChange(false);
                    },
                    onError: (err) => toast.error(err.message),
                  })
                }
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>
          <div className="flex gap-2">
            {isAdmin ? (
              <>
                <Button
                  variant={note.selected ? "default" : "outline"}
                  size="sm"
                  onClick={() =>
                    select.mutate(
                      { id: note.id, selected: !note.selected },
                      { onError: (err) => toast.error(err.message) },
                    )
                  }
                >
                  <Star
                    className={cn(
                      "mr-1 h-3.5 w-3.5",
                      note.selected && "fill-warning text-warning",
                    )}
                  />
                  {note.selected ? "Selected" : "Select as focus"}
                </Button>
                <Button
                  size="sm"
                  disabled={!!note.promoted_objective_id}
                  onClick={() => setPromoteOpen(true)}
                >
                  <ArrowUpRight className="mr-1 h-3.5 w-3.5" />
                  {note.promoted_objective_id ? "Promoted" : "Make objective"}
                </Button>
              </>
            ) : null}
          </div>
        </DialogFooter>

        <PromoteObjectiveDialog
          note={note}
          workspace={workspace}
          open={promoteOpen}
          onOpenChange={setPromoteOpen}
        />
      </DialogContent>
    </Dialog>
  );
}
